import { EXCHANGE_BOOKS } from "@/lib/book-stake";
import {
  getTeamRowName,
  getWhaleDemandFromOpposite,
  type APIMarket,
  type APIOpportunity,
  type BookData,
  type MarketOpportunity,
  type PeerOdds,
  type Side,
  type SideData,
} from "@/lib/whale-watch/whale-watch-shared";

export function getExchangeLiquidity(books: Record<string, BookData>): number {
  let total = 0;
  for (const [book, data] of Object.entries(books)) {
    if (EXCHANGE_BOOKS.has(book.toLowerCase())) {
      total += data.liquidity || 0;
    }
  }
  return total;
}

export function getExchangeActualMoneyAtRisk(books: Record<string, BookData>): number {
  let total = 0;
  for (const [book, data] of Object.entries(books)) {
    if (EXCHANGE_BOOKS.has(book.toLowerCase())) {
      total += data.actual_money_at_risk || 0;
    }
  }
  return total;
}

export function getBookStakeRequired(book: BookData): number {
  return book.stake_required ?? 0;
}

export function getSideWhaleSignal(books: Record<string, BookData>): number {
  let total = 0;
  for (const [bookName, book] of Object.entries(books)) {
    if (!EXCHANGE_BOOKS.has(bookName.toLowerCase())) continue;
    total += getBookStakeRequired(book);
  }
  return total;
}

export function getPinnacleLimit(books: Record<string, BookData>): number {
  const pinnacle = books.pinnacle || books.Pinnacle;
  return pinnacle?.liquidity || 0;
}

type SideNameFields = Pick<APIMarket, "entity_type" | "entity_name" | "home_team" | "away_team" | "line" | "event" | "image_url" | "home_logo_url" | "away_logo_url" | "id">;

export function sideDisplayName(side: Side["side"], market: SideNameFields): string {
  switch (side) {
    case "home": return market.home_team;
    case "away": return market.away_team;
    case "draw": return "Draw";
    case "yes": return "Yes";
    case "no": return "No";
    default:
      if (market.entity_type === "player" && market.entity_name) return market.entity_name;
      return (market.entity_type === "team" && getTeamRowName(market)) || `${side === "over" ? "Over" : "Under"} ${market.line}`;
  }
}

export function buildSide(
  sideData: SideData,
  market: APIMarket,
): Side<PeerOdds> & { readonly actual_money_at_risk: number } {
  let bestOdds = -Infinity;
  let bestBook = "";
  let bestBetLink: string | undefined;
  let bestBetLinkMobile: string | undefined;
  for (const peer of sideData.peers || []) {
    if (peer.odds_american > bestOdds) {
      bestOdds = peer.odds_american;
      bestBook = peer.book;
      bestBetLink = peer.bet_link || undefined;
      bestBetLinkMobile = peer.bet_link_mobile || undefined;
    }
  }

  if (!bestBook) {
    for (const [book, data] of Object.entries(sideData.books || {})) {
      if (data.odds_american > bestOdds) {
        bestOdds = data.odds_american;
        bestBook = book;
        bestBetLink = data.bet_link || undefined;
        bestBetLinkMobile = data.bet_link_mobile || undefined;
      }
    }
  }

  if (!bestBetLink && bestBook && sideData.books?.[bestBook]?.bet_link) {
    bestBetLink = sideData.books[bestBook].bet_link;
  }
  if (!bestBetLinkMobile && bestBook && sideData.books?.[bestBook]?.bet_link_mobile) {
    bestBetLinkMobile = sideData.books[bestBook].bet_link_mobile;
  }

  const sideType = sideData.side.toLowerCase() as Side["side"];
  return {
    name: sideDisplayName(sideType, market),
    line: sideData.line,
    side: sideType,
    total_liquidity: getExchangeLiquidity(sideData.books || {}),
    actual_money_at_risk: getExchangeActualMoneyAtRisk(sideData.books || {}) || getSideWhaleSignal(sideData.books || {}),
    pinnacle_limit: getPinnacleLimit(sideData.books || {}),
    books: sideData.books || {},
    peers: sideData.peers || [],
    best_odds: bestOdds,
    best_book: bestBook,
    best_bet_link: bestBetLink,
    best_bet_link_mobile: bestBetLinkMobile,
    demand: 0,
  };
}

export function mapServerOpportunities(opps: APIOpportunity[]): MarketOpportunity<PeerOdds>[] {
  const opportunities: MarketOpportunity<PeerOdds>[] = [];

  for (const opp of opps) {
    const market = opp.market_data;

    const recommendedSide = buildSide(opp.recommended_side, market);
    const oppositeSide = buildSide(opp.opposite_side, market);

    recommendedSide.demand = getWhaleDemandFromOpposite(oppositeSide.books);
    oppositeSide.demand = getWhaleDemandFromOpposite(recommendedSide.books);
    recommendedSide.best_book = opp.best_book;
    recommendedSide.best_odds = opp.best_odds;
    recommendedSide.edge_percent = opp.edge_percent;

    const serverBookPeer = recommendedSide.peers.find(
      (p) => p.book === opp.best_book
    );
    recommendedSide.best_bet_link =
      serverBookPeer?.bet_link ||
      recommendedSide.books[opp.best_book]?.bet_link ||
      undefined;
    recommendedSide.best_bet_link_mobile =
      serverBookPeer?.bet_link_mobile ||
      recommendedSide.books[opp.best_book]?.bet_link_mobile ||
      undefined;

    const crosswiseLiq = recommendedSide.actual_money_at_risk + oppositeSide.actual_money_at_risk;
    const maxWhaleSignal = Math.max(
      getSideWhaleSignal(recommendedSide.books),
      getSideWhaleSignal(oppositeSide.books)
    );
    const totalLiq = crosswiseLiq > 0 ? crosswiseLiq : maxWhaleSignal;

    let oppId = market.id;
    if (!oppId) {
      const entityKey = market.entity_name
        ? `-${market.entity_type}-${market.entity_name.toLowerCase().replace(/\s+/g, '_')}`
        : "";
      const sideAKey = market.side_a ? `${market.side_a.side}:${market.side_a.line}` : "none";
      const sideBKey = market.side_b ? `${market.side_b.side}:${market.side_b.line}` : "none";
      oppId = `${market.event_id}-${market.market}-${market.line}${entityKey}-${sideAKey}-${sideBKey}`;
    }

    let thirdSide: Side<PeerOdds> | undefined;
    if (market.market_arity === 3 && Array.isArray(market.sides)) {
      const used = new Set([opp.recommended_side.side, opp.opposite_side.side]);
      const remaining = market.sides.find((s) => s && !used.has(s.side));
      if (remaining) {
        thirdSide = buildSide(remaining, market);
      }
    }

    opportunities.push({
      id: oppId,
      event_id: market.event_id,
      event: market.event,
      event_start: market.event_start,
      home_team: market.home_team,
      away_team: market.away_team,
      league: market.league,
      sport: market.sport,
      market_type: market.market_type,
      updated_at: market.updated_at,
      entity_type: market.entity_type,
      entity_name: market.entity_name,
      image_url: market.image_url,
      home_logo_url: market.home_logo_url,
      away_logo_url: market.away_logo_url,
      market: {
        market: market.market,
        line: market.line,
        sides: [recommendedSide, oppositeSide],
        total_liquidity: totalLiq,
        pinnacle_limit: recommendedSide.pinnacle_limit + oppositeSide.pinnacle_limit,
        edge_supporting_liquidity: opp.edge_supporting_liquidity,
        same_side_conviction_liquidity: opp.same_side_conviction_liquidity,
        market_arity: market.market_arity,
        fair_outcome_share: opp.fair_outcome_share,
        third_side: thirdSide,
      },
    });
  }

  return opportunities;
}
